<!--
  Decorator to the "vue3-treeselect" library [1]

  =============================================================

  (?) When both set, native `openOnClick` and `openOnFocus` conflict
      themselves
        ↳ we have decided to pick `openOnClick` to `true` by default

  =============================================================

  ## `options` structure:

    (?) as the like of "bootstrap-vue-next", choice has been made on a semantic
        getting closer to the native HTML <option value=""> Text Node </option>

    ```script
      const options =
        [
          { value: 'foo', text: 'Hello!' },
          { value: 'bar', text: 'Goodbye.' },
        ]
    ```

  ## Use case: simple value

    ```template
      <ui-form-tree
        v-model="selected" // eg. 'foo'
        :options="options"
        single
      />
    ```

  ## Use case: multiple value

    ```template
      <ui-form-tree
        v-model="selected" // eg. [ 'foo', 'bar' ]
        :options="options"
        multiple
      />
    ```

  =============================================================

  @XXX "vue3-treeselect" STILL show the "clear" CTA even if value is an
       empty string AND showing no correspondance with the option fields
    ↳ workaround: use an `undefined` value to avoid this behavior

  @XXX "vue3-treeselect" events "deselect" and "input" not working anymore
   ↳ consequence: unable to deselect via native clearable feature
   ↳ monkey patch: manually setting up a clearable UX with added components

  @XXX "vue3-treeselect" not providing a dedicated filter for "clear" action
    ↳ workaround: `undefined` will be the next updated `modelValue`, manual
      filter can then be made on @update:model-value

  @XXX "vue3-treeselect" not allowing enabling of children if parent is
       disabled [2]

  @XXX "vue3-treeselect" not handling "Enter" key with ease
    ↳ patch surgery: hacking the core library to manually select the
      highlighted option on "Enter"

  @XXX "vue3-treeselect" not handling "Tab" event course throughout elements
       when :search + :single enabled
    ↳ description: striking "Tab" keyboard will close the menu, then lose its
      focus, then reset the tabindex (to the beggining of the page)
    ↳ patch surgery: listening to this event and **manually** set the focus into
      the next tabindex element

  @XXX "vue3-treeselect" not handling "Escape" event to close menu
    ↳ monkey patching: manually listener and trigger

  @XXX "vue3-treeselect" broken UX for :multiple + :searchable=false
    ↳ description: no keyboard event will work, instance method to show/hide
      the menu will not work either
    ↳ consequence: **no workaround** or **patch** found for this

  @XXX "vue3-treeselect" broken UX for :multiple + :searchable=true
    ↳ description: "Enter" keyboard event will (mostly) not work
    ↳ consequence: **no workaround** or **patch** found for this

  @XXX "vue3-treeselect" not closing menu when opening another
       "vue3-treeselect" Component
    ↳ patch surgery: manually closing the menu in such case

  @XXX "vue3-treeselect" not closing menu if selected options
        is identical
  @XXX "vue3-treeselect" triggers "@select" but not "@update:modelValue"
        when selected options is identical
    ↳ see according patches below

  @XXX "vue3-treeselect" used within <teleport> will throw errors on unmounted
       hooks
    ↳ cause: hard to say, even with the use of `:append-to-body` props
      at this moment, this component is so smelly that the only safe state of
      mind is to ditch it
    ↳ workaround (in some cases): use vanilla HTML `HTMLElement.remove()` to
      avoid trigger of the "unmounted" hook (→ totally unsafe pattern...)

  @XXX "vue3-treeselect" unreliably triggers multiple `@update:model-value`
    ↳ cause: unknown, erratic
    ↳ empirical workaround: if so, use `@select` event

  @TODO: completely unreliable Component → we should find another one
  @TODO: while working on a new component, since we update this one for
         TypeScript compliancy, it would be best to create two sets of
         component managing the single/multiple feature
  @TODO: also after decomposing single/multiple component, we could set up
         a VueComponent Generic to enhance typing and avoir some clunky
         reassignation

  =============================================================

  @link https://github.com/zanmato/vue3-treeselect [1]
  @link https://github.com/riophae/vue-treeselect/pull/147 [2]
-->
<template>
  <ui-form-inner-button-icon
    :class="[
      'ui-form-tree-clearable',
      `ui-form-tree-clearable--${size}`,
      `ui-form-tree-clearable--${status}`,
    ]"
    :container-class="elementClassToString(attrs.class as ElementClass | undefined) ?? ''"
    :hide="!(isClearableCTAShown)"
    placement="right"
    name="clear"
    :size="UI.SIZE.SMALL"
    :elevation="UI.ELEVATION.LEVEL_0"
    :variant="clearableVariant"
    @click="fixClearFeature"
  >
    <treeselect
      ref="treeselect"
      v-bind="dependencyInjection"
      v-click-outside="fixClosingMenu"
      :multiple="proxyMultiple"
      :open-on-click="openOnClick"
      :open-on-focus="!plaintext"
      :disabled="isTreeselectInteractivityLocked"
      :no-results-text="noResultsText"
      :no-options-text="noOptionsText"
      :limit-text="limitText"
      :clearable="false"
      :searchable="searchable"
      :placeholder="placeholder"
      :class="[
        'ui-form-tree',
        `ui-form-tree--${status}`,
        `ui-form-tree--${size}`,
        clearable ? 'ui-form-tree-clearable' : '',
        clearableVariant ? `ui-form-tree--clearable-${clearableVariant}` : '',
        indent ? 'ui-form-tree--indent-options' : '',
        noResultHidden ? 'ui-form-tree--hide-no-results' : '',
        {
          'ui-form-tree--plaintext': plaintext,
        }
      ]"
      @update:model-value="onMultipleSelect"
      @select="fixIdenticValueSubmit"
      @search-change="(query) => search = query"
      @keydown.enter.stop.prevent="onSubmitMenuSearchedOption"
      @keydown.tab="emitSearchedOptionOnTab"
      @keydown.esc.stop.prevent="onEscapeIntent"
    >
      <!--
        @vue-skip
        @XXX Volar + Options API + $slots injection
        @link https://github.com/vuejs/core/issues/5312
      -->
      <template v-for="(_, name_) in $slots" #[name_]="data">
        <slot :name="name_" v-bind="{ ...data, search }" />
      </template>
    </treeselect>
  </ui-form-inner-button-icon>
</template>

<style lang="sass">
@import '~/assets/sass/modules/form/variables'

.ui-form-tree
  // "bootstrap" create <select> height according to font size, which we have
  // to simulate here
  $min-height: 38px
  // Different container patterns from "bootstrap", so we have to empirically
  // set padding according to our needs
  $offset-x: 6px
  $placeholder-offset-y: 1px

  .vue3-treeselect__control
    width: auto // remove 100% default value
    min-height: $min-height
    padding-left: $offset-x
    padding-right: $input-padding-x
    font-family: $input-font-family
    font-size: $input-font-size
    font-weight: $input-font-weight
    line-height: $input-line-height
    background-color: $input-bg
    color: $input-color
    border-color: $input-border-color
    border-width: $input-border-width
    border-collapse: separate // ("vue3-treeselect" value being reset by "bootstrap5")
    @include transition($input-transition)
    @include border-radius($input-border-radius, 0)

  // (minor correction)
  &.vue3-treeselect--focused:not(.vue3-treeselect--open) .vue3-treeselect__control
    border-color: $input-border-color
    box-shadow: initial

  .vue3-treeselect__placeholder
    color: $input-placeholder-color

  .vue3-treeselect__single-value
    color: $input-color

  &--enabled,
  &--muted
    &.vue3-treeselect--single:not(.vue3-treeselect--searchable)
      @include clickable

  .vue3-treeselect__list-item *
    @include clickable

  /// (!) Fixing "vue-treeselect" disabled attribute strange width behavior
  &.vue3-treeselect--disabled .vue3-treeselect__single-value
    width: inherit

  // Removing default blue colours for selected items
  .vue3-treeselect__multi-value-item
    background-color: $gray-97
    color: $input-color
    font-size: 90%

    &:hover
      transition: $transition-base
      background-color: mix-alpha($input-focus-border-color, 10%)

      .vue3-treeselect__value-remove
        color: $danger

  .vue3-treeselect__option
    display: flex
    align-items: center

  .vue3-treeselect__value-remove
    color: $gray-dark

  .vue3-treeselect__label-container
    display: inline-flex
    align-items: center

  .vue3-treeselect__label
    padding-left: initial

  .vue3-treeselect__menu
    width: inherit

  // Hide no-results text when prop is enabled
  &--hide-no-results
    .vue3-treeselect__tip
      display: none

  // Add gap between options checkbox and label
  .vue3-treeselect__checkbox-container
    display: inline-block
    margin-left: space-x(.25)
    margin-right: space-x(.75)

  // Avoid gap on inner options
  .vue3-treeselect__option-arrow-placeholder
    display: none

  /// Clearable icon
  .vue3-treeselect__x-container
    padding-right: space-x(1.5)
    color: $clearable-icon-color

  @each $theme, $value in $palette-semantic

    &--clearable-#{$theme} .vue3-treeselect__x-container
      color: $value

  /// Indenting
  &--indent-options

    .vue3-treeselect__indent-level-0 > .vue3-treeselect__option

      .vue3-treeselect__checkbox-container,
      .vue3-treeselect__label-container
        padding-left: space-x(.5)
        padding-right: space-x(2.25)


    .vue3-treeselect__indent-level-1 > .vue3-treeselect__option

      .vue3-treeselect__checkbox-container,
      .vue3-treeselect__label-container
        $left: 18px // (empirical: according to "vue3-treeselect" checkbox)

        padding-left: $left
        padding-right: space-x(2.25)

  /// Status
  &--disabled

    .vue3-treeselect__control
      background-color: $input-disabled-bg

    .vue3-treeselect__single-value,
    .vue3-treeselect__multi-value-item
      color: $input-disabled-color

  &--idled

    .vue3-treeselect__control
      border-style: $input-idled-border-style

  &--plaintext
    @include precede
      cursor: text

    /// Plaintext locks the control via `:disabled` (keyboard-safe); neutralize
    /// the library default greyed-out "disabled" chrome.
    &.vue3-treeselect--disabled

      .vue3-treeselect__control
        background-color: $input-bg
        opacity: 1

      .vue3-treeselect__single-value,
      .vue3-treeselect__multi-value-item
        color: $input-color

    .vue3-treeselect__control
      border-color: transparent
      pointer-events: none

    .vue3-treeselect__control-arrow-container
      opacity: 0

  /// Shadowing
  &.vue3-treeselect.vue3-treeselect--open:not(.vue3-treeselect--disabled):not(.vue3-treeselect--focused) .vue3-treeselect__control
    border-color: $input-focus-border-color
    box-shadow: $shadow-cel-shading

  &.vue3-treeselect--open-above .vue3-treeselect__menu
    // @NOTE: micro managing the cel shadow position to avoid blank line
    // (tweaking $shadow-cel-shading)
    box-shadow: 5px 6px 0px -1px rgba(0, 0, 0, 0.5)

  &.vue3-treeselect--open-below .vue3-treeselect__menu
    // @NOTE: micro managing the cel shadow position to avoid blank line
    // (tweaking $shadow-cel-shading)
    box-shadow: 4px 4px 0px 0px rgba(0, 0, 0, 0.5)

  /// (!) Resolve focus border non displayed when the result list appears
  ///     above the input
  &.vue3-treeselect--open-below.vue3-treeselect--open .vue3-treeselect__menu-container
    border-top: 1px solid $input-focus-border-color

  /// (!) Resolve wrong HTML cascading with z-index micro managing
  &.vue3-treeselect--open.vue3-treeselect--open
    z-index: z-index($z-indexes, 'main', 'float')
  &.vue3-treeselect--open.vue3-treeselect--open .vue3-treeselect__menu-container
    z-index: auto !important // override needed
  &.vue3-treeselect--open-above.vue3-treeselect--open .vue3-treeselect__menu
    z-index: -1

  /// Reworking layout (via "flex" API)
  .vue3-treeselect__multi-value
    display: inline-flex
    flex-wrap: wrap
    align-items: center
    margin-bottom: initial

  .vue3-treeselect__multi-value-item-container
    display: inline-flex
    align-items: center
    padding-top: initial
    padding-right: initial

  .vue3-treeselect__multi-value-item
    padding-top: initial
    padding-bottom: initial

  &.vue3-treeselect--searchable.vue3-treeselect--multi.vue3-treeselect--has-value .vue3-treeselect__input-container
    padding-top: initial


  /// Sizing
  &--sm
    $gap: 2px
    $min-height: 31px
    $input-height-sm: 25px

    .vue3-treeselect__control
      min-height: $min-height
      height: $min-height
      @include font-size($input-font-size-sm)
      @include border-radius($input-border-radius-sm)

    .vue3-treeselect__single-value
      transform: translateY(- $gap)
      padding-left: $gap
      line-height: $min-height
      height: $input-height-sm

    .vue3-treeselect__input-container
      margin-top: - $gap
      margin-bottom: - $gap

    .vue3-treeselect__input
      font-size: $font-size-sm

    .vue3-treeselect__placeholder
      margin-top: - $gap

    .vue3-treeselect__multi-value
      gap: $gap * 2
      padding-top: $gap * 2
      padding-bottom: $gap * 2

  &--md
    $gap: 1px
    $min-height: 38px

    .vue3-treeselect__control
      min-height: $min-height
      height: $min-height
      @include font-size($input-font-size)
      @include border-radius($input-border-radius)

    .vue3-treeselect__single-value
      transform: translateY(- $gap)
      line-height: $min-height

    .vue3-treeselect__input-container
      margin-top: - $gap
      margin-bottom: - $gap

    .vue3-treeselect__input
      font-size: $input-font-size

    .vue3-treeselect__placeholder
      $offset-y: 1px
      margin-top: $offset-y
      font-size: $input-font-size

    .vue3-treeselect__multi-value
      gap: $gap * 2
      padding-top: $gap * 2
      padding-bottom: $gap * 2

///////////////////////////////

/// Clearable
///
$offset-x: -21px

.ui-form-tree-clearable.ui-form-inner-button-icon__cta
  // (empirical micro-management, normally made onto the
  // <ui-form-inner-button-icon>)

  transform: translateX($offset-x)

/// Sizing
///
.ui-form-tree-clearable--sm.ui-form-inner-button-icon__cta
  $offset-y: -3px

  transform: translateY($offset-y) translateX($offset-x)

/// Status
///
.ui-form-tree-clearable--disabled.ui-form-inner-button-icon__cta,
.ui-form-tree-clearable--idled.ui-form-inner-button-icon__cta,
.ui-form-tree-clearable--deferred.ui-form-inner-button-icon__cta
  @include no-interaction

</style>

<script setup lang="ts">
import Treeselect from '@zanmato/vue3-treeselect'
import { useLegacyRootNode } from '~/composables/core/dom'
import { querySelector2 } from '~/utils/lib/core/dom'
import { elementClassToString } from '~/utils/lib/core/helpers'
import {
  __,
  always,
  append,
  applySpec,
  delay,
  equals,
  has,
  identity,
  includes,
  isEmpty,
  isUndefined,
  lengthEq,
  lensProp,
  map,
  Maybe,
  merge,
  not,
  omit,
  over,
  pipe,
  pipe_,
  renameKey_,
  uniq,
  when,
} from '~/utils/lib/core/prelude'
import type { ElementClass } from '~/utils/lib/core/types_'
import { focusNextElement, type VARIANT, type SIZE, type STATUS } from '~/utils/lib/layout/ui'

/// @XXX "vue3" template generic not working → could have been useful here

/**
 * @desc Structuring "vue3-treeselect" `node` typing
 * @link https://zanmato.github.io/vue3-treeselect/#node
 */
export type UITreeOption<T = any, U = T>
  = { value: string|number|boolean
    , text?: string
    , children?: UITreeOption<U>[]
    , isDisabled?: boolean
    , isDefaultExpanded?: boolean
    , raw?: T
    }
/**
 * @desc `mempty` value for <ui-form-tree> ie. not selection made
 *  (pseudo maybe pattern)
 */
export type UITreeMempty = undefined

/**
 * @XXX "vue3-treeselect" not having proper typing → copy/paste documentation
 *    ↳ patch surgery: default values are handled internally by
 *      "vue3-treeselect", hence no need to define them again (disable warning)
 *    ↳ monkey patch (1): unknown `node` typing set as `UITreeOption`
 *    ↳ monkey patch (2): unknown `id` typing set as `string`
 *    ↳ monkey patch (3): YAGNI - remove `loadOptions`
 * @link https://zanmato.github.io/vue3-treeselect/#props
 */
type ComponentProps = {
  // Default: false	Whether to allow resetting value even if there are disabled selected nodes.
  allowClearingDisabled?: boolean
  // Default: false	When an ancestor node is selected/deselected, whether its disabled descendants should be selected/deselected. You may want to use this in conjunction with allowClearingDisabled prop.
  allowSelectingDisabledDescendants?: boolean
  // Default: false	Whether the menu should be always open.
  alwaysOpen?: boolean
  // Default: false	Append the menu to <body />.
  appendToBody?: boolean
  // Default: false	Whether to enable async search mode.
  async?: boolean
  // Default: false	Automatically focus the component on mount.
  autoFocus?: boolean
  // Default: true	Automatically load root options on mount. When set to false, root options will be loaded when the menu is opened.
  autoLoadRootOptions?: boolean
  // Default: false	When user deselects a node, automatically deselect its ancestors. Applies to flat mode only.
  autoDeselectAncestors?: boolean
  // Default: false	When user deselects a node, automatically deselect its descendants. Applies to flat mode only.
  autoDeselectDescendants?: boolean
  // Default: false	When user selects a node, automatically select its ancestors. Applies to flat mode only.
  autoSelectAncestors?: boolean
  // Default: false	When user selects a node, automatically select its descendants. Applies to flat mode only.
  autoSelectDescendants?: boolean
  // Default: true	Whether Backspace removes the last item if there is no text input.
  backspaceRemoves?: boolean
  // Default: () => true	Function that processes before clearing all input fields. Return false to stop values being cleared.
  // eslint-disable-next-line vue/require-default-prop
  beforeClearAll?: () => (boolean | Promise<boolean>)
  // Default: false	Show branch nodes before leaf nodes.
  branchNodesFirst?: boolean
  // Default: true	Whether to cache results of each search request for async search mode.
  cacheOptions?: boolean
  // Default: true	Whether to show an "×" button that resets value.
  clearable?: boolean
  // Default: "Clear all"	Title for the "×" button when :multiple="true".
  // eslint-disable-next-line vue/require-default-prop
  clearAllText?: string
  // Default: Defaults to false when :multiple="true"; always true otherwise.	Whether to clear the search input after selecting an option. Use only when :multiple="true". For single-select mode, it always clears the input after selecting regardless of the prop value.
  clearOnSelect?: boolean
  // Default: "Clear value"	Title for the "×" button.
  // eslint-disable-next-line vue/require-default-prop
  clearValueText?: string
  // Default: true	Whether to close the menu after selecting an option. Use only when :multiple="true".
  closeOnSelect?: boolean
  // Default: 0	How many levels of branch nodes should be automatically expanded when loaded. Set Infinity to make all branch nodes expanded by default.
  // eslint-disable-next-line vue/require-default-prop
  defaultExpandLevel?: number
  // Default: true	Whether Delete removes the last item if there is no text input.
  deleteRemoves?: boolean
  // Default: ","	Delimiter to use to join multiple values for the hidden field value.
  // eslint-disable-next-line vue/require-default-prop
  delimiter?: string
  // Default: false	Whether to flatten the tree when searching (sync search mode only). See here for example.
  flattenSearchResults?: boolean
  // Default: false	Whether to prevent branch nodes from being selected. See here for example.
  disableBranchNodes?: boolean
  // Default: false	Whether to disable the control or not.
  disabled?: boolean
  // Default: false	Set to true to disable the fuzzy matching functionality, which is enabled by default.
  disableFuzzyMatching?: boolean
  // Default: false	Whether to enable flat mode or not. See here for detailed information.
  flat?: boolean
  // Default: "<auto-incrementing number>$$"	Will be passed with all events as the last param. Useful for identifying events origin.
  // eslint-disable-next-line vue/require-default-prop
  instanceId?: string|number
  // Default: false	Joins multiple values into a single form field with the delimiter (legacy mode).
  joinValues?: boolean
  // Default: Infinity	Limit the display of selected options. The rest will be hidden within the limitText string.
  // eslint-disable-next-line vue/require-default-prop
  limit?: number
  // Default: count => `and ${count} more`	Function that processes the message shown when selected elements pass the defined limit.
  limitText?: (count :number) => string
  // Default: "Loading..."	Text displayed when loading options.
  // eslint-disable-next-line vue/require-default-prop
  loadingText?: string
  // Default: –	Used for dynamically loading options. See here for detailed information.
  // Possible values of action: "LOAD_ROOT_OPTIONS", "LOAD_CHILDREN_OPTIONS" or "ASYNC_SEARCH".
  // callback - a function that accepts an optional error argument
  // parentNode - only presents when loading children options
  // searchQuery - only presents when searching async options
  // instanceId - eqauls to the value of instanceId prop you passed to vue3-treeselect
  // loadOptions: ({action, callback, parentNode?, instanceId}) => (void | Promise)
  // Default: [ "label" ]	Which keys of a node object to filter on.
  // eslint-disable-next-line vue/require-default-prop
  matchKeys?: string[]
  // Default: 300	Sets maxHeight style value of the menu.
  // eslint-disable-next-line vue/require-default-prop
  maxHeight?: number
  // Default: false	Set true to allow selecting multiple options (a.k.a., multi-select mode).
  multiple?: boolean
  // Default: –	Generates a hidden <input /> tag with this field name for html forms.
  // eslint-disable-next-line vue/require-default-prop
  name?: string
  // Default: "No sub-options."	Text displayed when a branch node has no children.
  noChildrenText?: string
  // Default: "No options available."	Text displayed when there are no available options.
  noOptionsText?: string
  // Default: "No results found..."	Text displayed when there are no matching search results.
  noResultsText?: string
  // Default: node => node	Used for normalizing source data. See here for detailed information.
  // eslint-disable-next-line vue/require-default-prop
  normalizer?: (node, instanceId) => UITreeOption
  // Default: "auto"	By default ("auto"), the menu will open below the control. If there is not enough space, vue3-treeselect will automatically flip the menu. You can use one of other four options to force the menu to be always opened to specified direction.
  // Acceptable values: "auto", "below", "bottom", "above" or "top".
  // eslint-disable-next-line vue/require-default-prop
  openDirection?: string
  // Default: true	Whether to automatically open the menu when the control is clicked.
  openOnClick?: boolean
  // Default: false	Whether to automatically open the menu when the control is focused.
  openOnFocus?: boolean
  // Default: –	Array of available options. See here to learn how to define them.
  // @NOTE: make it required
  options: UITreeOption[]
  // Default: "Select..."	Field placeholder, displayed when there's no value.
  placeholder?: string
  // Default: false	Applies HTML5 required attribute when needed.
  required?: boolean
  // Default: "Retry?"	Text displayed asking user whether to retry loading children options.
  // eslint-disable-next-line vue/require-default-prop
  retryText?: string
  // Default: "Click to retry"	Title for the retry button.
  // eslint-disable-next-line vue/require-default-prop
  retryTitle?: string
  // Default: true	Whether to enable searching feature or not.
  searchable?: boolean
  // Default: false	Set true if the search query should search in all ancestor nodes too. See here for example.
  searchNested?: boolean
  // Default: "Type to search..."	Text tip to prompt for async search. Used for async search mode.
  // eslint-disable-next-line vue/require-default-prop
  searchPromptText?: string
  // Default: false	Whether to show a children count next to the label of each branch node. See here for example.
  showCount?: boolean
  // Default: "ALL_CHILDREN"	Used in conjunction with showCount to specify which type of count number should be displayed.
  // Acceptable values: "ALL_CHILDREN", "ALL_DESCENDANTS", "LEAF_CHILDREN", "LEAF_DESCENDANTS".
  // eslint-disable-next-line vue/require-default-prop
  showCountOf?: string
  // Default: –	Whether to show children count when searching. Fallbacks to the value of showCount when not specified.
  showCountOnSearch?: boolean
  // Default: "ORDER_SELECTED"	In which order the selected options should be displayed in trigger & sorted in value array. Use only when :multiple="true". See here for example.
  // Acceptable values: "ORDER_SELECTED", "LEVEL" or "INDEX".
  // eslint-disable-next-line vue/require-default-prop
  sortValueBy?: string
  // Default: 0	Tab index of the control.
  // eslint-disable-next-line vue/require-default-prop
  tabIndex?: number
  // Default: –	The value of the control.
  // Should be id or node object when :multiple="false", or an array of id or node object when :multiple="true". Its format depends on the valueFormat prop.
  // For most cases, just use v-model instead.
  // eslint-disable-next-line vue/require-default-prop
  modelValue?: UITreeOption["value"] | UITreeOption["value"][]
  // Default: "BRANCH_PRIORITY"	Which kind of nodes should be included in the value array in multi-select mode. See here for example.
  // Acceptable values: "ALL", "BRANCH_PRIORITY", "LEAF_PRIORITY" or "ALL_WITH_INDETERMINATE".
  // eslint-disable-next-line vue/require-default-prop
  valueConsistsOf?: string
  // Default: "id"	Format of value prop.
  // Note that, when set to "object", only id & label properties are required in each node object in value.
  // Acceptable values: "id" or "object".
  // eslint-disable-next-line vue/require-default-prop
  valueFormat?: string
  // Default: 999 z-index of the menu.
  // eslint-disable-next-line vue/require-default-prop
  zIndex?: string | number
}

type UITreeProps
  = ComponentProps
  & { status? :STATUS
    , size? :SIZE
    // decorating `multiple` as a tuple `multiple/single` (DX)
    , single? :boolean
    , clearableVariant? :VARIANT|undefined
    // cf. component comment
    // furthermore, it might useful to let the "tab" KeyboardEvent bubbling
    , noTabBehaviorFix? :boolean
    // UI indenting nested options
    , indent? :boolean
    // Hide the no-results text when searching
    , noResultHidden? :boolean
    // Plaintext mode
    , plaintext? :boolean
    }

/**
 * @XXX "vue3-treeselect" not having proper typing → copy/paste documentation
type ComponentEmits
 * @link https://zanmato.github.io/vue3-treeselect/#events
 */
type ComponentEmits = {
  // Emitted when the menu opens.
  "open": []
  // Emitted when the menu closes.
  "close": [UITreeOption["value"]]
  // Emitted after value changes.
  "input": [UITreeOption["value"]|UITreeOption["value"][]|UITreeMempty]
  // Emitted after selecting an option.
  "select": [UITreeOption]
  // Emitted after deselecting an option.
  "deselect": [UITreeOption]
  // Emitted after the search query changes.
  "search-change": [string]
}

type UITreeEmits
  = { "update:model-value": [UITreeOption["value"]|UITreeOption["value"][]|UITreeMempty]
    , "tab:searched-option": [UITreeOption["value"]|UITreeMempty]
    }

/// Setup
///

const props = withDefaults(defineProps<UITreeProps>(), {
  status: 'enabled',
  size: 'md',
  multiple: false,
  single: false,
  searchable: false,
  openOnClick: true,
  openOnFocus: false,
  closeOnSelect: true,
  noResultsText: 'aucun résultat',
  noOptionsText: 'aucun résultat',
  noChildrenText: 'aucun résultat',
  limitText: count => `(+ ${count})`,
  noTabBehaviorFix: false,
  clearableVariant: undefined,
  placeholder: '',
  indent: false,
  noResultHidden: false,
  plaintext: false,
})

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()

const refTreeselect = useTemplateRef<InstanceType<typeof Treeselect>>('treeselect')
const getRootNode = useLegacyRootNode()

const emit = defineEmits<ComponentEmits & UITreeEmits>()

defineSlots<{
  'after-list'?: (props: { search: string }) => any;
  // @TODO: add the other slots
}>()

const search = ref('')

/// Hooks
///

const dependencyInjection = computed(() => {
  const parseValue = renameKey_('value', 'id')
  const parseText = renameKey_('text', 'label')
  /// @XXX "vue-treeselect" adds every unknown keys into a dedicated
  ///      `raw` property → thus leading to a cumbersome typing where
  ///      options is an array of Open-Record
  ///    ↳ patch surgery: cf. `UITreeOption` as we have reuse the `raw` key
  ///      but while providing a pseudo-maybe value rather than an
  ///      Open-Record
  const parseRaw = item => pipe_(
    item,
    when<any, any>(
      has('raw'),
      pipe(
        omit([ 'raw' ]),
        merge(__, item.raw)
      )
    )
  )

  const lens_children = lensProp<UITreeOption, "children">('children')

  const options = map.iterable(
    pipe(
      // "item.value" → "item.id"
      // "item.text" → "item.label"
      parseValue,
      parseText,
      parseRaw,
      when(
        has('children'),
        over(
          lens_children,
          pipe(
            when(isUndefined, always([])),
            // "item.children.value" → "item.children.id"
            // "item.children.text" → "item.children.label"
            map.iterable(
              pipe(
                parseValue,
                parseText,
                parseRaw,
              )
            )
          )
        )
      )
    ),
    props.options
  )

  const modelValue = (() => {
    const isMultiple = props.single ? false : props.multiple

    if (isMultiple)
      return Array.isArray(props.modelValue) ? props.modelValue : []

    return Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue
  })()

  return {
    ...props,
    ...attrs,
    options,
    modelValue,
  }
})

const hasDisabledStatusType = computed(() => {
  return includes(
      props.status,
      [
        'disabled',
        'idled',
        'deffered',
      ] satisfies STATUS[]
    )
})

/// Underlying treeselect must be disabled for `plaintext` (CSS alone is not
/// enough: focus + `open-on-focus` could still open the menu and change value).
const isTreeselectInteractivityLocked = computed(() => {
  return hasDisabledStatusType.value || props.plaintext
})

const proxyMultiple = computed(() => {
  switch (true) {
    // intentionaly choosing a single option (bc. default `single` eq <F>)
    case props.single:
      return false
    // otherwise relying on multiple attr
    default:
      return props.multiple
  }
})

const isClearableCTAShown = computed(() => {
  switch (true) {

    case props.plaintext:
      return false

    case not(props.clearable):
      return false

    case proxyMultiple.value &&
         lengthEq(0, (props.modelValue as [])):
      return false

    case not(proxyMultiple.value) &&
         equals(props.modelValue, undefined satisfies UITreeMempty):
      return false

    default:
      return true
  }
})

onBeforeUnmount(async() => {
  /// @XXX "vue3-treeselect" used within <teleport> will throw errors on unmounted
  ///      hooks
  /// ↳ workaround: use vanilla HTML `HTMLElement.remove()` to
  ///   avoid trigger of the "unmounted" hook (→ totally unsafe pattern...)
  Maybe.toMaybe( refTreeselect.value )
    .chain( getRootNode<HTMLElement> )
    .map(el => el.remove())

  await delay(0) // (defensive: `HTMLElement.remove` to fully effects)
})

/// Methods
///

/// Adapters
defineExpose({
  refTreeselect,
  focus() {
    if (props.plaintext)
      return

    Maybe.toMaybe( refTreeselect.value )
      .map(ref => {
        ref.openMenu()
        ref.focusInput()
      })
  },
  blur() {
    Maybe.toMaybe( refTreeselect.value )
      .map(ref => ref.blurInput() )
  },
})

/// @XXX "vue3-treeselect" not handling "Enter" key with ease
///   ↳ patch surgery: hacking the core library to manually select the
///     highlighted option on "Enter"
/** @desc /!\ intricate intimacy */
function onSubmitMenuSearchedOption() {
  // (while menu is not open, the "Enter" event should just open it)
  const isMenuOpened = Maybe.toMaybe( refTreeselect.value )
    .chain(
      instance => instance.menu.isOpen ?
        Maybe.Just(true) :
        Maybe.Nothing()
    )

  if (Maybe.isNothing(isMenuOpened)) {
    Maybe.toMaybe( refTreeselect.value )
      .map(instance => instance.openMenu())

    return
  }

  const selector = '.vue3-treeselect__option--highlight'

  const mSearchedOption = Maybe.toMaybe( refTreeselect.value )
    .chain( getRootNode<HTMLElement> )
    .chain(
      el => querySelector2(selector, el)
    )
    .chain(
      el => Maybe.toMaybe( el.getAttribute('data-id') )
    )

  if (Maybe.isNothing(mSearchedOption))
    return

  const searchOptions = Maybe.fromJust(mSearchedOption)

  Maybe.toMaybe( refTreeselect.value )
    .map(async (instance) => {

      const upd = (() => {
        switch (instance.multiple) {

          case false:
            return searchOptions

          case true:
            return pipe_(
              instance.modelValue,
              when<any, string[]>(
                isEmpty,
                always([])
              ),
              append(searchOptions),
              uniq,
            )

        }
      })()

      if (props.status === 'enabled')
        emit('update:model-value', upd  )

      instance.blurInput()
      await nextTick()
      instance.focusInput()
    })
}
/// (?) Added event triggered on "Tab" key, provide useful information
///     (does we want to save the highlighted value ?)
///       ↳ @NOTE: could also be designed as a prop ":save-on-tab"
function emitSearchedOptionOnTab(event: KeyboardEvent) {
  // @XXX "vue3-treeselect" not handling "Tab" event course throughout elements
  //      when :search + :single enabled
  // (cf. Comment section)
  if
    (  not(event.shiftKey)
    && not(props.noTabBehaviorFix)
    )
    {
      focusNextElement()
      event.preventDefault()
    }

  const selector = '.vue3-treeselect__option--highlight'

  const mSearchedOption = Maybe.toMaybe( refTreeselect.value )
    .chain( getRootNode<HTMLElement> )
    .chain(
      el => querySelector2(
        selector,
        el
      )
    )
    .chain(
      el => Maybe.toMaybe( el.getAttribute('data-id') )
    )

  if (Maybe.isNothing(mSearchedOption))
    return

  const value = Maybe.fromJust(mSearchedOption)

  if (props.status === 'enabled')
    emit('tab:searched-option', value)
}
/// @XXX "vue3-treeselect" not handling "Escape" event to close menu
///  ↳ monkey patching: manually listener and trigger
function onEscapeIntent() {
  Maybe.toMaybe( refTreeselect.value )
    .map(instance => {
      if (instance.menu.isOpen)
        instance.closeMenu()
    })
}
/// @XXX "vue3-treeselect" not closing menu when opening another
///      "vue3-treeselect" Component
///  ↳ patch surgery: manually closing the menu in such case
/** @desc /!\ intricate intimacy  */
function fixClosingMenu() {
  Maybe.toMaybe( refTreeselect.value )
    .map(instance => {
      if (instance.menu.isOpen)
        instance.closeMenu()
    })
}
/// @XXX "vue3-treeselect" events "deselect" and "input" not working anymore
///   ↳ consequence: unable to deselect via native clearable feature
///   ↳ monkey patch: manually setting up a clearable UX with added components
function fixClearFeature() {
  if (props.status !== 'enabled')
    return

  const upd = (() => {
    switch (proxyMultiple.value) {
      case false: return undefined satisfies UITreeMempty
      case true: return [] satisfies UITreeOption["value"][]
    }
  })()

  emit('update:model-value', upd)
}
/// @XXX "vue3-treeselect" not closing menu if selected options
///      is identical
/// @XXX "vue3-treeselect" triggers "@select" but not "@update:modelValue"
///      when selected options is identical
///   ↳ monkey patch: here we just trigger manually the
///     "@update:modelValue" accordingly on "@select"
function fixIdenticValueSubmit(item) {
  if
    (
      (props.status !== 'enabled') ||
      (proxyMultiple.value === true)
    )
    return

  const item_ = renameKey_('id', 'value', item) as UITreeOption

  Maybe.toMaybe( refTreeselect.value )
    .map(instance => {
      const selectedValue = item_.value
      const currentValue = instance.modelValue

      if (selectedValue !== currentValue)
        emit('update:model-value', selectedValue)
    })

  emit('select', item_)
}
/// @NOTE: honestly at this stage I do not know if we are monkey patching
///        "vue3-treeselect" flaws OR our own monkey patches that need
///        monkey patches
function onMultipleSelect(items) {
  if
    (
      (props.status !== 'enabled') ||
      (proxyMultiple.value !== true)
    )
    return

  /// @XXX Swallowing `[]` used to avoid a spurious emit in older treeselect builds,
  ///     but it breaks the controlled v-model contract: internal state clears while
  ///     the parent still holds the previous ids, so the next render re-applies them
  ///     and the chip stays selected.
  emit('update:model-value', items)
}

</script>

<script lang="ts">
/**
 * @function wrap
 * @return {Array<UITreeOption>}
 * @example
 *  wrap([ 'foo' ])
 *  // [{ value: 'foo', text: 'foo' }]
 */
 export const wrap
 // @ts-ignore: UNSAFE coerce → propose a `transform` mapping with 2 template variables?
  = (list: string[]) :UITreeOption[] => map.iterable(
    applySpec({
      value: identity,
      text: identity
    }),
    list
  )

</script>
